import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useRepo, useServices } from './services';
import { ClubContext, useClub, type ClubContextValue } from './club';
import { Shell, type NavSection } from './Shell';
import {
  canAccessClubRoute,
  canAccessPlatformRoute,
  clubNavFor,
  effectiveClubRole,
  effectiveClubRoles,
  platformNavFor,
  ROLE_LABEL,
  type ClubRouteKey,
  type PlatformRouteKey,
  type RoleContext,
} from '../lib/roles';
import { useDoors, useLocks, useOrganization, useSyncJobs } from '../data/hooks';
import { doorSyncStatuses, doorsAwaitingSync } from '../lib/sync';
import { Button, Dialog, EmptyState, ErrorState, Field, Loading } from '../components/ui';
import type { IconName } from '../components/Icon';
import { errorText } from '../lib/errors';

/** Brand-UI-icoon per navigatie-item (alleen weergave; routes en rechten komen uit lib/roles). */
const NAV_ICON: Record<string, IconName> = {
  dashboard: 'locatie',
  doors: 'deur',
  persons: 'persoon',
  groups: 'groep',
  codes: 'sleutel',
  loans: 'sleutel',
  logbook: 'historie',
  import: 'groep',
  settings: 'instellingen',
  help: 'hulp',
  clubs: 'locatie',
  inventory: 'slot',
  installers: 'persoon',
  team: 'groep',
  alerts: 'melding',
  faq: 'hulp',
  audit: 'historie',
};

const impersonationKey = (orgId: string) => `antreja.impersonation.${orgId}`;
const impersonationWriteKey = (orgId: string) => `antreja.impersonation.write.${orgId}`;

function readImpersonationWrite(orgId: string): boolean {
  try {
    return sessionStorage.getItem(impersonationWriteKey(orgId)) === '1';
  } catch {
    return false;
  }
}

function readImpersonation(orgId: string): boolean {
  try {
    return sessionStorage.getItem(impersonationKey(orgId)) === '1';
  } catch {
    return false;
  }
}

export function Forbidden() {
  return (
    <EmptyState title="Geen toegang tot dit scherm" action={<Link to="/">Terug naar start</Link>}>
      Je rol geeft geen toegang tot deze pagina. Denk je dat dit niet klopt? Vraag het je clubbeheerder.
    </EmptyState>
  );
}

export function ClubGuard({ route, children }: { route: ClubRouteKey; children: ReactNode }) {
  const { roleCtx } = useClub();
  return canAccessClubRoute(route, roleCtx) ? <>{children}</> : <Forbidden />;
}

export function PlatformGuard({ route, children }: { route: PlatformRouteKey; children: ReactNode }) {
  const { session } = useAuth();
  const ctx: RoleContext = { platformRole: session?.platformRole ?? null, orgRole: null };
  return canAccessPlatformRoute(route, ctx) ? <>{children}</> : <Forbidden />;
}

function platformSections(ctx: RoleContext): NavSection[] {
  const items = platformNavFor(ctx);
  return items.length ? [{ title: 'Platform', items: items.map((i) => ({ to: `/platform/${i.path}`, label: i.label, icon: NAV_ICON[i.key] })) }] : [];
}

function WriteDialog({ orgName, onClose, onConfirm }: { orgName: string; onClose: () => void; onConfirm: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Wijzigen in ${orgName}`} description="Normaal wijzigt de club zelf. Doe dit alleen op verzoek van de club of in een noodgeval. Je reden komt in het logboek van de club.">
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await onConfirm(reason);
          } catch (err) {
            setError(errorText(err, 'Kon wijzigen niet inschakelen'));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="Reden" hint="Bijvoorbeeld: op verzoek van de secretaris een vrijwilliger toegevoegd" error={error}>
          {(p) => <textarea {...p} className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} required />}
        </Field>
        <div className="dialog-actions">
          <Button type="button" onClick={onClose}>
            Annuleren
          </Button>
          <Button variant="primary" type="submit" disabled={busy}>
            Wijzigen inschakelen
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ImpersonationGate({ orgName, onStart }: { orgName: string; onStart: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="panel panel-pad" style={{ maxWidth: 560 }}>
      <h1 style={{ fontSize: '2rem', marginBottom: 8 }}>Meekijken bij {orgName}</h1>
      <p className="muted" style={{ marginBottom: 20 }}>
        Je kijkt mee in een club als Antreja-medewerker, eerst alleen lezen. Dat wordt vastgelegd in het logboek van de club, met je reden.
      </p>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await onStart(reason);
          } catch (err) {
            setError(errorText(err, 'Kon niet starten'));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="Reden" hint="Bijvoorbeeld: supportvraag over pas van de penningmeester" error={error}>
          {(p) => <textarea {...p} className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} required />}
        </Field>
        <div className="row">
          <Button variant="primary" type="submit" disabled={busy}>
            Start meekijken
          </Button>
          <Link to="/platform/clubs">Annuleren</Link>
        </div>
      </form>
    </div>
  );
}

export function ClubLayout() {
  const { orgId = '' } = useParams();
  const { session } = useAuth();
  const repo = useRepo();
  const { mode } = useServices();
  const navigate = useNavigate();
  const orgQ = useOrganization(orgId);
  const [impersonated, setImpersonated] = useState(() => readImpersonation(orgId));
  const [writeEnabled, setWriteEnabled] = useState(() => readImpersonationWrite(orgId));
  const [askWrite, setAskWrite] = useState(false);
  useEffect(() => {
    setImpersonated(readImpersonation(orgId));
    setWriteEnabled(readImpersonationWrite(orgId));
  }, [orgId]);

  const membership = session?.memberships.find((m) => m.organization_id === orgId) ?? null;
  const platformRole = session?.platformRole ?? null;
  // Antreja-installateur of clubinstallateur (de sessie bevat alleen geldige toewijzingen).
  const installerAssigned = (session?.installerOrgIds ?? []).includes(orgId);
  const roleCtx: RoleContext = useMemo(
    () => ({ platformRole, orgRole: membership?.role ?? null, installerAssigned }),
    [platformRole, membership?.role, installerAssigned],
  );
  const role = effectiveClubRole(roleCtx);
  // Platformbeheer en support kijken mee (gelogd); een toegewezen installateur werkt gewoon in de club.
  const isPlatformViewer = (platformRole === 'platform_admin' || platformRole === 'support') && !membership;

  useEffect(() => {
    if (role && mode === 'demo') {
      (repo as unknown as { setActor?: (id: string, r: string) => void }).setActor?.(session!.profile.id, role);
    }
  }, [role, mode, repo, session]);

  if (orgQ.isLoading) return <Shell sections={[]} mobileTitle="Antreja"><Loading /></Shell>;
  if (orgQ.error) return <Shell sections={[]} mobileTitle="Antreja"><ErrorState error={orgQ.error} /></Shell>;
  const org = orgQ.data;
  if (!org || !role) {
    return (
      <Shell sections={platformSections(roleCtx)} mobileTitle="Antreja">
        <EmptyState title="Deze club is niet beschikbaar" action={<Link to="/">Kies een club</Link>}>
          De club bestaat niet of je bent er geen beheerder van.
        </EmptyState>
      </Shell>
    );
  }

  const ctx: ClubContextValue = {
    org,
    orgId,
    role,
    roles: effectiveClubRoles(roleCtx),
    roleCtx,
    managedGroupIds: session?.managedGroupIds ?? [],
    impersonating: isPlatformViewer,
    // Meekijken is standaard alleen lezen; een Antreja-beheerder schakelt wijzigen expliciet in (gelogd).
    readOnly: role === 'support' || (isPlatformViewer && !writeEnabled),
  };

  const stopImpersonation = () => {
    try {
      sessionStorage.removeItem(impersonationKey(orgId));
      sessionStorage.removeItem(impersonationWriteKey(orgId));
    } catch {
      /* ignore */
    }
    navigate('/platform/clubs');
  };

  if (isPlatformViewer && !impersonated) {
    return (
      <Shell sections={platformSections(roleCtx)} mobileTitle="Meekijken">
        <ImpersonationGate
          orgName={org.name}
          onStart={async (reason) => {
            await repo.startImpersonation(orgId, reason);
            try {
              sessionStorage.setItem(impersonationKey(orgId), '1');
            } catch {
              /* ignore */
            }
            setImpersonated(true);
          }}
        />
      </Shell>
    );
  }

  const multipleClubs = portalClubIds(session).length > 1;
  const home = `/c/${orgId}/${clubNavFor(roleCtx)[0]?.path ?? 'dashboard'}`;
  return (
    <ClubContext.Provider value={ctx}>
      <ClubShell
        // Meekijken: geen clubblok in het menu; de banner bovenaan zegt al bij welke club (met "Stop meekijken").
        clubSwitch={
          isPlatformViewer
            ? undefined
            : { label: multipleClubs ? 'Club wisselen' : ROLE_LABEL[role], name: org.name, to: multipleClubs ? '/clubs' : home }
        }
        extraSections={platformSections(roleCtx)}
        banners={
          isPlatformViewer ? (
            <div className="banner banner-impersonate" role="note">
              <span>
                {writeEnabled ? (
                  <>
                    Je werkt in <strong>{org.name}</strong> als Antreja-beheerder. Wijzigingen worden gelogd.
                  </>
                ) : (
                  <>
                    Je kijkt mee in <strong>{org.name}</strong> (alleen lezen). Dit wordt gelogd.
                  </>
                )}
              </span>
              <span className="row" style={{ gap: 8 }}>
                {platformRole === 'platform_admin' && !writeEnabled && (
                  <Button size="sm" onClick={() => setAskWrite(true)}>
                    Wijzigen inschakelen
                  </Button>
                )}
                <Button size="sm" onClick={stopImpersonation}>
                  Stop meekijken
                </Button>
              </span>
              {askWrite && (
                <WriteDialog
                  orgName={org.name}
                  onClose={() => setAskWrite(false)}
                  onConfirm={async (reason) => {
                    await repo.enableImpersonationWrite(orgId, reason);
                    try {
                      sessionStorage.setItem(impersonationWriteKey(orgId), '1');
                    } catch {
                      /* ignore */
                    }
                    setWriteEnabled(true);
                    setAskWrite(false);
                  }}
                />
              )}
            </div>
          ) : null
        }
      />
    </ClubContext.Provider>
  );
}

function ClubShell({
  clubSwitch,
  extraSections,
  banners,
}: {
  clubSwitch?: { label: string; name: string; to: string };
  extraSections: NavSection[];
  banners: ReactNode;
}) {
  const { orgId, org, roleCtx, role } = useClub();
  const doors = useDoors(orgId);
  const locks = useLocks(orgId);
  const canSeeSync = role !== 'group_manager';
  const jobs = useSyncJobs(canSeeSync ? orgId : '__none__');
  const waiting = canSeeSync && doors.data && locks.data && jobs.data ? doorsAwaitingSync(doorSyncStatuses(doors.data, locks.data, jobs.data)).length : 0;

  const items = clubNavFor(roleCtx).map((n) => ({
    to: `/c/${orgId}/${n.path}`,
    label: n.label,
    icon: NAV_ICON[n.key],
    count: n.key === 'doors' ? waiting : undefined,
    countLabel: n.key === 'doors' ? `${waiting} deuren wachten op sync` : undefined,
  }));
  return (
    <Shell sections={[{ title: org.name, items }, ...extraSections]} clubSwitch={clubSwitch} banners={banners} mobileTitle={org.name}>
      <Outlet />
    </Shell>
  );
}

/** Clubs waar dit account het portaal gebruikt: club_admin/group_manager-lidmaatschap of installateurstoewijzing. */
export function portalClubIds(session: { memberships: { organization_id: string; role: string }[]; installerOrgIds?: string[]; platformRole: string | null } | null): string[] {
  if (!session) return [];
  const ids = session.memberships.filter((m) => m.role === 'club_admin' || m.role === 'group_manager').map((m) => m.organization_id);
  ids.push(...(session.installerOrgIds ?? []));
  return [...new Set(ids)];
}

/** /c/:orgId → eerste scherm dat deze rol mag zien (installateur: Deuren). */
export function ClubIndex() {
  const { orgId, roleCtx } = useClub();
  return <Navigate to={`/c/${orgId}/${clubNavFor(roleCtx)[0]?.path ?? 'dashboard'}`} replace />;
}

export function PlatformLayout() {
  const { session } = useAuth();
  const ctx: RoleContext = { platformRole: session?.platformRole ?? null, orgRole: null };
  if (!platformNavFor(ctx).length) return <Navigate to="/" replace />;
  return (
    <Shell sections={platformSections(ctx)} mobileTitle="Platform">
      <Outlet />
    </Shell>
  );
}
